// Klondike solitaire engine — pure functions, no React, no DOM.
//
// The React layer treats this module as a black box:
//   const state = newGame(seed);
//   const moves = legalMoves(state);
//   const next  = applyMove(state, moves[i]);
//
// Later the server imports the same module to validate move sequences, so
// keep everything here browser-agnostic.
//
// WIRE FORMAT: a game's move history is a flat array of integers, each the
// index of the chosen move within legalMoves(state) at that step —
// including draws, one integer per draw. Deliberately NOT run-length
// encoded: this caps the damage any single stored integer can do on
// replay, at the cost of a few extra bytes for draw-heavy games.
// stripTrailingDraws drops trailing stock cycling on abandon. The
// enumeration order in legalMoves IS the schema — never reorder it without
// a coordinated version bump.
//
// Undo is a protocol affair, not a move: the server pops the last index
// from the history (single-step, never twice in a row), so stored
// histories never contain undone moves and this engine needs no inverse-
// move machinery.
//
// =============================================================================
// CARDS
// =============================================================================
//
// A card is an integer 0..51:
//
//   card = suit * 13 + rank
//   suit: 0 spade, 1 heart, 2 club, 3 diamond   (matches sprite-sheet row order)
//   rank: 0 A, 1 2, 2 3, ..., 9 10, 10 J, 11 Q, 12 K
//
// A full deck is Array.from({length: 52}, (_, i) => i). Helpers below convert
// to/from the "heart-10" sprite keys used by <Tile>; the engine itself never
// touches those strings.

export const SUIT = Object.freeze({ SPADE: 0, HEART: 1, CLUB: 2, DIAMOND: 3 });
export const COLOR = Object.freeze({ BLACK: 0, RED: 1 });
export const RANK = Object.freeze({ ACE: 0, JACK: 10, QUEEN: 11, KING: 12 });

const SUIT_NAMES = ["spade", "heart", "club", "diamond"];
const RANK_NAMES = [
  "A",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
];

export function cardSuit(card) {
  return Math.floor(card / 13);
}
export function cardRank(card) {
  return card % 13;
}
export function cardColor(card) {
  const s = cardSuit(card);
  return s === SUIT.SPADE || s === SUIT.CLUB ? COLOR.BLACK : COLOR.RED;
}

export function spriteKey(card) {
  return `${SUIT_NAMES[cardSuit(card)]}-${RANK_NAMES[cardRank(card)]}`;
}

// =============================================================================
// STATE SHAPE
// =============================================================================
//
//   state = {
//     stock: number[],          // face-down. index 0 = next card to draw.
//     waste: number[],          // face-up. last element = visible top.
//     tableau: [                // 7 columns, index 0 = leftmost.
//       { faceDown: number[], faceUp: number[] },
//       ...
//     ],                        // faceUp last element = visually-topmost card.
//     foundations: [            // 4 slots. Any suit may start any empty slot,
//       number[], number[],     // then that slot holds one suit A..K.
//       number[], number[],
//     ],
//     drawSize: 3,              // fixed at 3 for MS-style; on state for future
//                               // variant flex.
//   }
//
// Stock/waste ordering:
//   Draw: shift stock[0..drawSize-1] in order and push to waste; last pushed
//         is visible top.
//   Recycle (draw when stock is empty): stock = [...waste]; waste = []. No
//         reversal — waste's bottom-to-top order becomes stock's top-to-bottom,
//         so the draw sequence repeats identically across cycles.

// =============================================================================
// MOVES
// =============================================================================

export const PILE = Object.freeze({
  WASTE: "waste",
  TABLEAU: "tableau",
  FOUNDATION: "foundation",
});
export const MOVE_TYPE = Object.freeze({ DRAW: "draw", MOVE: "move" });

//   { type: MOVE_TYPE.DRAW }
//       Draws drawSize from stock to waste, OR recycles waste->stock if stock
//       is empty. Legal iff stock.length + waste.length > 0.
//
//   { type: MOVE_TYPE.MOVE, from, to }
//       from: { pile: PILE.WASTE }
//           | { pile: PILE.TABLEAU,    index: 0..6, depth: 0..N-1 }
//           | { pile: PILE.FOUNDATION, index: 0..3 }
//       to:   { pile: PILE.TABLEAU,    index: 0..6 }
//           | { pile: PILE.FOUNDATION, index: 0..3 }
//
//   `depth` on a tableau source: 0 grabs just the visually-topmost face-up
//   card (single-card move); depth D grabs the card at
//   faceUp[faceUp.length-1-D] plus everything visually above it. So depth
//   faceUp.length-1 grabs the entire face-up run. Count of cards moved is
//   derivable and not carried on the move.

// =============================================================================
// CANONICAL MOVE ORDER  (effectively wire format — do not reorder)
// =============================================================================
//
// legalMoves(state) returns moves in exactly this order:
//
//   1. { type: DRAW } — if legal.
//   2. Waste top (if non-empty): destinations in DEST_ORDER.
//   3. For each tableau column i in 0..6:
//        For each depth d in 0..faceUp.length-1 (small run first):
//          destinations in DEST_ORDER.
//   4. For each foundation i in 0..3 (if non-empty):
//        destinations in DEST_ORDER, TABLEAU targets only
//        (foundation->foundation moves are never emitted — never useful).
//
// DEST_ORDER for a movable card/run:
//   a. Foundations 0..3 in index order (only for single-card sources).
//   b. Tableau columns 0..6 in index order.
//
// No dedup of equivalent empty destinations: every legal foundation slot and
// every empty tableau column is emitted distinctly so the player's visual
// choice survives into replay.
//
// Auto-flip: when a move exposes a face-down tableau card, it flips to
// face-up inside applyMove. Never a user-visible move.

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

// Deck generation: game ID -> deck is a fixed bijection. The ID is mapped
// to a rank in [0, 52!) by multiplication with MIX (a constant coprime to
// 52!, derived from the golden ratio: floor(52! / phi) bumped to the next
// coprime integer), then the rank is unranked into a permutation via the
// factorial number system (Lehmer code). The MIX step exists because IDs
// are sequential and raw unranking of consecutive integers only permutes
// the tail of the deck — consecutive games would get nearly identical
// deals.
//
// FROZEN: changing FACT52/MIX or the unranking order silently changes every
// stored game's deck. The Go server must reproduce this exactly (math/big);
// the known-answer test in solitaire.test.js is the cross-language fixture.
const FACT52 =
  80658175170943878571660636856403766975289505440883277824000000000000n;
const MIX =
  49849493726186177023735136051197689539309098536019687269722921809751n;

// factorials 0!..51! for unranking
const FACTORIALS = (() => {
  const f = [1n];
  for (let i = 1n; i <= 51n; i++) f.push(f[f.length - 1] * i);
  return f;
})();

// rank (BigInt in [0, 52!)) -> permutation of card ints 0..51.
function unrankDeck(rank) {
  const pool = Array.from({ length: 52 }, (_, i) => i);
  const deck = [];
  let r = rank;
  for (let i = 51; i >= 0; i--) {
    const f = FACTORIALS[i];
    const idx = Number(r / f);
    r %= f;
    deck.push(pool.splice(idx, 1)[0]);
  }
  return deck;
}

function deckForSeed(seed) {
  // Normalize into [0, 52!) — negative or huge seeds still map somewhere
  // deterministic rather than throwing.
  const id = ((BigInt(Math.trunc(seed)) % FACT52) + FACT52) % FACT52;
  return unrankDeck((id * MIX) % FACT52);
}

function cloneState(s) {
  return {
    stock: s.stock.slice(),
    waste: s.waste.slice(),
    tableau: s.tableau.map((c) => ({
      faceDown: c.faceDown.slice(),
      faceUp: c.faceUp.slice(),
    })),
    foundations: s.foundations.map((f) => f.slice()),
    drawSize: s.drawSize,
    fanSize: s.fanSize ?? 0,
  };
}

// fanSize after a move. Visual — not needed for rules — but bundled into
// engine state so the caller never has to maintain it separately. Classic
// MS behavior: fresh draw-3 shows up to three fanned cards; as the player
// plays them off, the fan shrinks; when the stock empties and recycles,
// the fan collapses to zero until the next draw refills it.
function computeNextFanSize(prevFan, prevState, move) {
  if (move.type === MOVE_TYPE.DRAW) {
    return prevState.stock.length > 0
      ? Math.min(prevState.drawSize, prevState.stock.length)
      : 0;
  }
  if (move.type === MOVE_TYPE.MOVE && move.from.pile === PILE.WASTE) {
    return Math.max(0, prevFan - 1);
  }
  return prevFan;
}

// Cards that `from` would move, in bottom-to-top order (bottom is the card
// that lands on the destination). Returns null for piles that have nothing
// movable at the requested position (empty waste, depth past end of run,
// empty foundation) — those are the legitimate "no such move" signals; a
// malformed `from` is a bug and is allowed to crash on property access.
function sourceCards(state, from) {
  if (from.pile === PILE.WASTE) {
    if (state.waste.length === 0) return null;
    return [state.waste[state.waste.length - 1]];
  } else if (from.pile === PILE.TABLEAU) {
    const col = state.tableau[from.index];
    const len = col.faceUp.length;
    if (from.depth < 0 || from.depth >= len) return null;
    return col.faceUp.slice(len - 1 - from.depth);
  } else if (from.pile === PILE.FOUNDATION) {
    const f = state.foundations[from.index];
    if (f.length === 0) return null;
    return [f[f.length - 1]];
  }
  return null;
}

function canDropOnTableau(state, tableauIdx, card) {
  const col = state.tableau[tableauIdx];
  if (col.faceUp.length === 0) return cardRank(card) === RANK.KING;
  const top = col.faceUp[col.faceUp.length - 1];
  return (
    cardColor(top) !== cardColor(card) && cardRank(top) === cardRank(card) + 1
  );
}

function canDropOnFoundation(state, foundIdx, card) {
  const f = state.foundations[foundIdx];
  if (f.length === 0) return cardRank(card) === RANK.ACE;
  const top = f[f.length - 1];
  return (
    cardSuit(top) === cardSuit(card) && cardRank(top) + 1 === cardRank(card)
  );
}

function isLegalMove(state, move) {
  if (move.type === MOVE_TYPE.DRAW) {
    return state.stock.length + state.waste.length > 0;
  }
  if (move.type !== MOVE_TYPE.MOVE) return false;
  const run = sourceCards(state, move.from);
  if (!run) return false;
  const bottom = run[0];
  const to = move.to;
  // Foundation-to-foundation is never emitted.
  if (move.from.pile === PILE.FOUNDATION && to.pile === PILE.FOUNDATION) {
    return false;
  }

  // Multi-card runs only land on the tableau.
  if (run.length > 1 && to.pile !== PILE.TABLEAU) return false;

  if (to.pile === PILE.TABLEAU) {
    if (move.from.pile === PILE.TABLEAU && move.from.index === to.index) {
      // no self-move
      return false;
    }
    return canDropOnTableau(state, to.index, bottom);
  } else if (to.pile === PILE.FOUNDATION) {
    return canDropOnFoundation(state, to.index, bottom);
  }
  return false;
}

// =============================================================================
// PUBLIC API
// =============================================================================

// Fresh 52-card deck via the seed->deck bijection (see deckForSeed above),
// standard Klondike deal (1..7 cards per column, top card face-up), rest in
// the stock.
export function newGame(seed = 0) {
  const deck = deckForSeed(seed);
  const tableau = Array.from({ length: 7 }, () => ({
    faceDown: [],
    faceUp: [],
  }));
  let idx = 0;
  for (let col = 0; col < 7; col++) {
    for (let row = 0; row <= col; row++) {
      const card = deck[idx++];
      if (row < col) tableau[col].faceDown.push(card);
      else tableau[col].faceUp.push(card);
    }
  }
  return {
    stock: deck.slice(idx),
    waste: [],
    tableau,
    foundations: [[], [], [], []],
    drawSize: 3,
    fanSize: 0,
  };
}

// Ordered list of legal moves per the canonical order above. Pure.
export function legalMoves(state) {
  const moves = [];

  if (state.stock.length + state.waste.length > 0) {
    moves.push({ type: MOVE_TYPE.DRAW });
  }

  if (state.waste.length > 0) {
    const top = state.waste[state.waste.length - 1];
    const from = { pile: PILE.WASTE };
    for (let f = 0; f < 4; f++) {
      if (canDropOnFoundation(state, f, top)) {
        moves.push({
          type: MOVE_TYPE.MOVE,
          from,
          to: { pile: PILE.FOUNDATION, index: f },
        });
      }
    }
    for (let t = 0; t < 7; t++) {
      if (canDropOnTableau(state, t, top)) {
        moves.push({
          type: MOVE_TYPE.MOVE,
          from,
          to: { pile: PILE.TABLEAU, index: t },
        });
      }
    }
  }

  for (let i = 0; i < 7; i++) {
    const col = state.tableau[i];
    for (let d = 0; d < col.faceUp.length; d++) {
      const bottom = col.faceUp[col.faceUp.length - 1 - d];
      const from = { pile: PILE.TABLEAU, index: i, depth: d };
      if (d === 0) {
        for (let f = 0; f < 4; f++) {
          if (canDropOnFoundation(state, f, bottom)) {
            moves.push({
              type: MOVE_TYPE.MOVE,
              from,
              to: { pile: PILE.FOUNDATION, index: f },
            });
          }
        }
      }
      for (let t = 0; t < 7; t++) {
        if (t === i) continue;
        if (canDropOnTableau(state, t, bottom)) {
          moves.push({
            type: MOVE_TYPE.MOVE,
            from,
            to: { pile: PILE.TABLEAU, index: t },
          });
        }
      }
    }
  }

  for (let i = 0; i < 4; i++) {
    const f = state.foundations[i];
    if (f.length === 0) continue;
    const top = f[f.length - 1];
    const from = { pile: PILE.FOUNDATION, index: i };
    for (let t = 0; t < 7; t++) {
      if (canDropOnTableau(state, t, top)) {
        moves.push({
          type: MOVE_TYPE.MOVE,
          from,
          to: { pile: PILE.TABLEAU, index: t },
        });
      }
    }
  }

  return moves;
}

// Returns a new state with `move` applied. Handles auto-flip. Throws on
// illegal moves.
export function applyMove(state, move) {
  if (!isLegalMove(state, move)) {
    throw new Error(`Illegal move: ${JSON.stringify(move)}`);
  }
  const next = cloneState(state);
  next.fanSize = computeNextFanSize(state.fanSize ?? 0, state, move);

  if (move.type === MOVE_TYPE.DRAW) {
    if (next.stock.length === 0) {
      next.stock = next.waste;
      next.waste = [];
    } else {
      const n = Math.min(next.drawSize, next.stock.length);
      const drawn = next.stock.splice(0, n);
      next.waste.push(...drawn);
    }
    return next;
  }

  const { from, to } = move;
  let cards;
  if (from.pile === PILE.WASTE) {
    cards = [next.waste.pop()];
  } else if (from.pile === PILE.TABLEAU) {
    const col = next.tableau[from.index];
    const count = from.depth + 1;
    cards = col.faceUp.splice(col.faceUp.length - count, count);
    if (col.faceUp.length === 0 && col.faceDown.length > 0) {
      col.faceUp.push(col.faceDown.pop());
    }
  } else if (from.pile === PILE.FOUNDATION) {
    cards = [next.foundations[from.index].pop()];
  } else {
    throw new Error(`Invalid from pile: ${from.pile}`);
  }

  if (to.pile === PILE.TABLEAU) {
    next.tableau[to.index].faceUp.push(...cards);
  } else {
    next.foundations[to.index].push(...cards);
  }

  return next;
}

export function tryApply(state, move) {
  try {
    return applyMove(state, move);
  } catch {
    return null;
  }
}

// Convenience: wire format is indices into legalMoves(state).
export function applyMoveByIndex(state, index) {
  const moves = legalMoves(state);
  if (index < 0 || index >= moves.length) {
    throw new Error(`Move index out of range: ${index}`);
  }
  return applyMove(state, moves[index]);
}

function movesEqual(a, b) {
  if (a.type !== b.type) return false;
  if (a.type === MOVE_TYPE.DRAW) return true;
  return (
    a.from.pile === b.from.pile &&
    a.from.index === b.from.index &&
    a.from.depth === b.from.depth &&
    a.to.pile === b.to.pile &&
    a.to.index === b.to.index
  );
}

// Like tryApply, but also returns the move's position in legalMoves(state) —
// the wire-format index used when relaying moves to the server.
//
// UI clicks produce semantic move objects (from/to piles) with no
// knowledge of legalMoves()'s canonical ordering, so the wire index has to
// be looked up by matching against the enumeration.
export function tryApplyWithIndex(state, move) {
  const moves = legalMoves(state);
  for (let i = 0; i < moves.length; i++) {
    if (movesEqual(move, moves[i])) {
      return { next: applyMove(state, moves[i]), index: i };
    }
  }
  return null;
}

// Start from a fresh deck for `seed` and apply a sequence of wire-format
// move indices. On a bad index, logs and returns the partial state reached
// so far — callers prefer a partial game to losing the slot entirely.
// `label` tags the log line so the source is obvious.
export function replay(seed, moveIndices, { label = "replay" } = {}) {
  let s = newGame(seed);
  for (let i = 0; i < moveIndices.length; i++) {
    const mv = legalMoves(s)[moveIndices[i]];
    if (!mv) {
      console.error(
        `${label}: bad move index ${moveIndices[i]} at position ${i} for seed ${seed}`,
      );
      return s;
    }
    s = applyMove(s, mv);
  }
  return s;
}

// For abandoned-game storage: drop trailing DRAW moves (pure stock cycling
// after the last board-changing move) so histories don't end in dead
// cycling. A bad index truncates there, same as replay() — the kept
// prefix still has its trailing draws stripped, even for corrupt histories.
export function stripTrailingDraws(seed, moveIndices) {
  let s = newGame(seed);
  let lastProductive = -1;
  for (let i = 0; i < moveIndices.length; i++) {
    const mv = legalMoves(s)[moveIndices[i]];
    if (!mv) break;
    if (mv.type !== MOVE_TYPE.DRAW) lastProductive = i;
    s = applyMove(s, mv);
  }
  return moveIndices.slice(0, lastProductive + 1);
}

// All four foundations hold 13 cards.
export function isWon(state) {
  return state.foundations.every((f) => f.length === 13);
}

// Falls back to 1 when state.fanSize is zero but the waste isn't, so the
// top card stays visible after the fan has been played off.
export function wasteVisibleCount(state) {
  if (state.waste.length === 0) return 0;
  return Math.min(Math.max(1, state.fanSize ?? 0), state.waste.length);
}

// The stock is a "dead end" when clicking it can only cycle through the
// same cards — i.e. the stock is empty, the waste has at most drawSize
// cards, AND every one of them is already fanned face-up. When the fan
// shows fewer than the waste holds (cards were played off a fuller fan),
// recycling + drawing re-fans the hidden ones, which is not a dead end.
// Display-only: never consulted by legalMoves, so the wire format doesn't
// move.
export function isStockDeadEnd(state) {
  return (
    state.stock.length === 0 &&
    state.waste.length > 0 &&
    state.waste.length <= state.drawSize &&
    wasteVisibleCount(state) === state.waste.length
  );
}
